import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import { usePwaInstall } from 'hooks/use-pwa-install';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * PWA install button – only renders on mobile viewports.
 * On Android/Chrome it triggers the native install prompt.
 * On iOS it shows a step-by-step "Add to Home Screen" dialog.
 * Hidden when the app is already running in standalone (installed) mode.
 */
const PwaInstallButton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canInstall, isStandalone, isIOS, install } = usePwaInstall();
  const { t } = useTranslation();
  const [iosDialogOpen, setIosDialogOpen] = useState(false);

  // Only render on mobile
  if (!isMobile) return null;
  // Already installed – nothing to show
  if (isStandalone) return null;
  // Desktop browser without install support and not iOS – hide
  if (!canInstall && !isIOS) return null;

  const handleClick = async () => {
    if (isIOS) {
      setIosDialogOpen(true);
    } else {
      await install();
    }
  };

  return (
    <>
      <PaiTooltip title={t('pwaInstall.tooltip')} placement="bottom">
        <IconButton size="small" onClick={handleClick} sx={{ color: 'primary.main' }}>
          <InstallMobileIcon fontSize="small" />
        </IconButton>
      </PaiTooltip>

      {/* iOS manual installation guide */}
      <Dialog open={iosDialogOpen} onClose={() => setIosDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{t('pwaInstall.iosTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('pwaInstall.iosSubtitle')}
            </Typography>

            <Divider />

            <Stack spacing={1.5}>
              <IOSStep step={1} text={t('pwaInstall.iosStep1')} emoji="⬆️" />
              <IOSStep step={2} text={t('pwaInstall.iosStep2')} emoji="📲" />
              <IOSStep step={3} text={t('pwaInstall.iosStep3')} emoji="✅" />
            </Stack>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={() => setIosDialogOpen(false)} size="small">
              {t('pwaInstall.close')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface IOSStepProps {
  step: number;
  text: string;
  emoji: string;
}

const IOSStep: React.FC<IOSStepProps> = ({ step, text, emoji }) => (
  <Stack direction="row" alignItems="flex-start" gap={1.5}>
    <Box
      sx={{
        minWidth: 24,
        height: 24,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {step}
    </Box>
    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
      {emoji} {text}
    </Typography>
  </Stack>
);

export default PwaInstallButton;
